/**
 * moc-newsletter.js
 * Mộc By Dylan — async newsletter subscription handler
 *
 * Wraps Shopify's native {% form 'customer' %} endpoint with:
 * - Progressive async fetch (no page reload)
 * - Inline brand-voice success / error states
 * - Loading state on the submit button
 * - Accessible aria-live announcements
 * - Graceful no-JS fallback (native form still submits normally)
 */

class MocNewsletterForm extends HTMLElement {
  /** @type {AbortController|null} */
  #abort = null;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this._onSubmit = this.#handleSubmit.bind(this);
    this.form.addEventListener('submit', this._onSubmit);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.form?.removeEventListener('submit', this._onSubmit);
  }

  // ─── Submit handler ────────────────────────────────────────────────────────

  async #handleSubmit(e) {
    e.preventDefault();

    // Prevent double-submit
    if (this.dataset.state === 'loading') return;

    // Client-side email validation (calm inline error)
    const input = this.querySelector('input[type="email"]');
    const email = input?.value?.trim() ?? '';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showError('Vui lòng nhập địa chỉ email hợp lệ.');
      input?.focus();
      return;
    }

    this.#setState('loading');
    this.#abort = new AbortController();

    try {
      // Strip URL fragment from action — Shopify's {% form 'customer' %} may
      // append #fragment (e.g. /contact#mcd-notify-xyz). getAttribute gives the
      // raw attribute value so we can reliably strip the fragment ourselves.
      const rawAction = this.form.getAttribute('action') || '/contact';
      const action = rawAction.split('#')[0] || '/contact';

      // Encode as application/x-www-form-urlencoded — the same Content-Type
      // that a native form submit produces and that Shopify's /contact endpoint
      // expects. Using FormData (multipart/form-data) causes Shopify to reject
      // the request with a non-2xx response.
      const payload = new URLSearchParams(new FormData(this.form)).toString();

      const res = await fetch(action, {
        method: 'POST',
        body: payload,
        signal: this.#abort.signal,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      // Shopify redirects (302→200) on success; fetch follows redirects so
      // res.ok is true when the subscription is processed. We also treat
      // res.redirected as success (same result, redirect was followed).
      if (res.ok || res.redirected) {
        this.#setState('success');
      } else {
        // Non-2xx without redirect — fall back to native submit so Shopify's
        // server-side error message renders (same behaviour as email-signup block).
        this.form.submit();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // Network error — fall back to native form submit.
        this.form.submit();
      }
    }
  }

  // ─── State helpers ─────────────────────────────────────────────────────────

  #setState(state) {
    this.dataset.state = state;

    const btn       = this.querySelector('[data-submit]');
    const btnLabel  = this.querySelector('[data-btn-label]');
    const spinner   = this.querySelector('[data-spinner]');
    const formRow   = this.querySelector('[data-form-row]');
    const successEl = this.querySelector('[data-success-state]');
    const errorEl   = this.querySelector('[data-error-state]');

    if (state === 'loading') {
      if (btn)      btn.disabled = true;
      if (btnLabel) btnLabel.hidden = true;
      if (spinner)  spinner.hidden = false;
      if (errorEl)  errorEl.hidden = true;
    } else if (state === 'success') {
      if (formRow)   formRow.hidden   = true;
      if (successEl) successEl.hidden = false;
      if (errorEl)   errorEl.hidden   = true;
      // Announce for screen readers
      successEl?.setAttribute('aria-live', 'polite');
    } else {
      // idle / reset
      if (btn)      btn.disabled = false;
      if (btnLabel) btnLabel.hidden = false;
      if (spinner)  spinner.hidden = true;
    }
  }

  #showError(msg) {
    const errorEl = this.querySelector('[data-error-state]');
    if (errorEl) {
      if (msg) {
        const p = errorEl.querySelector('p');
        if (p) p.textContent = msg;
      }
      errorEl.hidden = false;
    }
    this.#setState('idle');
  }
}

if (!customElements.get('moc-newsletter-form')) {
  customElements.define('moc-newsletter-form', MocNewsletterForm);
}
